import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";

const VALUES = [
  {
    title: "Trust",
    description:
      "Building long-term relationships through transparency and reliability.",
    Icon: HandshakeOutlinedIcon,
    accent: "#E91E8C",
    iconBg: "#FCE4EC",
  },
  {
    title: "Quality",
    description:
      "Focusing on quality across every property and service we provide.",
    Icon: VerifiedOutlinedIcon,
    accent: "#1E88E5",
    iconBg: "#E3F2FD",
  },
  {
    title: "Partnership",
    description:
      "Building strong relationships with customers, builders and developers.",
    Icon: GroupsOutlinedIcon,
    accent: "#43A047",
    iconBg: "#E8F5E9",
  },
  {
    title: "Growth",
    description: "Creating opportunities for sustainable real-estate growth.",
    Icon: TrendingUpOutlinedIcon,
    accent: "#FB8C00",
    iconBg: "#FFF3E0",
  },
  {
    title: "Value",
    description:
      "Helping customers find meaningful value in their real-estate decisions.",
    Icon: DiamondOutlinedIcon,
    accent: "#7B1FA2",
    iconBg: "#F3E5F5",
  },
];

const ValueCard = ({ title, description, Icon, accent, iconBg }) => (
  <Box
    sx={{
      width: "100%",
      bgcolor: "#fff",
      borderRadius: "12px",
      boxShadow: "0 8px 28px rgba(40, 20, 60, 0.08)",
      borderLeft: `5px solid ${accent}`,
      p: { xs: 2.25, sm: 2.5, md: 1.75, lg: 2.25 },
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      minHeight: { xs: 180, md: 168, lg: 180 },
      minWidth: 0,
      boxSizing: "border-box",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 12px 32px rgba(40, 20, 60, 0.12)",
      },
    }}
  >
    <Box
      sx={{
        width: { xs: 48, md: 42, lg: 48 },
        height: { xs: 48, md: 42, lg: 48 },
        borderRadius: "50%",
        bgcolor: iconBg,
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: { xs: 1.75, md: 1.25, lg: 1.75 },
      }}
    >
      <Icon sx={{ fontSize: { xs: 24, md: 22, lg: 24 } }} />
    </Box>

    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: "1.1rem", md: "0.95rem", lg: "1.05rem" },
        color: "#1A1A2E",
        mb: { xs: 1, md: 0.75 },
      }}
    >
      {title}
    </Typography>

    <Typography
      sx={{
        fontSize: { xs: 14, md: 12.5, lg: 13.5 },
        color: "#6B7280",
        lineHeight: { xs: 1.6, md: 1.55 },
        mb: { xs: 2, md: 1.5 },
        flexGrow: 1,
      }}
    >
      {description}
    </Typography>

    <Box
      sx={{
        width: 40,
        height: 4,
        borderRadius: 2,
        bgcolor: accent,
      }}
    />
  </Box>
);

const HomeValues = () => {
  const theme = useSelector((s) => s.themeValue?.colors) || {};
  const accent =
    theme.primary_gradient_end_color ||
    theme.primary_gradient_start_color ||
    "#7B1FA2";

  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "#F7F6F9",
        py: { xs: 5, md: 8 },
        px: { xs: 2, sm: 3 },
        overflow: "hidden",
      }}
    >
      {/* Soft skyline (top right) */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: { xs: -20, md: 40 },
          top: { xs: 24, md: 40 },
          width: { xs: 160, md: 260 },
          height: { xs: 50, md: 70 },
          opacity: 0.08,
          background: `linear-gradient(0deg, ${accent}, transparent)`,
          clipPath:
            "polygon(0% 100%, 10% 65%, 20% 80%, 30% 40%, 40% 60%, 50% 25%, 60% 50%, 70% 15%, 80% 45%, 90% 30%, 100% 55%, 100% 100%)",
          pointerEvents: "none",
        }}
      />
      {/* Soft dots (bottom left) */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: 24,
          bottom: 32,
          width: 80,
          height: 80,
          opacity: 0.12,
          backgroundImage: `radial-gradient(${accent} 1.5px, transparent 1.5px)`,
          backgroundSize: "14px 14px",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ maxWidth: 1140, mx: "auto", position: "relative", zIndex: 1 }}>
        <Stack alignItems="center" sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 36, height: 2, bgcolor: accent, borderRadius: 1 }} />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: accent,
              }}
            >
              Why Choose Us
            </Typography>
            <Box sx={{ width: 36, height: 2, bgcolor: accent, borderRadius: 1 }} />
          </Stack>

          <Typography
            align="center"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2rem" },
              color: "#1A1A2E",
              mb: 1.25,
            }}
          >
            Our Values
          </Typography>

          <Typography
            align="center"
            sx={{
              color: "#6B7280",
              fontSize: { xs: 14, sm: 15 },
              maxWidth: 520,
              lineHeight: 1.65,
              px: 1,
            }}
          >
            Guided by strong values, we build lasting relationships and create
            real value in every property journey.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            width: "100%",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(5, minmax(0, 1fr))",
            },
            gap: { xs: 2, sm: 2, md: 1.25, lg: 2 },
          }}
        >
          {VALUES.map((item) => (
            <Box
              key={item.title}
              sx={{ minWidth: 0, display: "flex" }}
            >
              <ValueCard {...item} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomeValues;
